import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import "../css/Admin_css/admin-customize.css";
import myToast from "../Utils/MyToast";
import SessionManager from "../Utils/SessionManager";
import MyLoader from "../Spinner/MyLoader";

const SETTING_DEFAULT_THERMAL_SAMPLE = "STT009";

//Created By Cursor — detect thermal printable samples
function isThermalSample(item) {
  const blob = `${item?.sample_name || ""} ${item?.file_name || ""} ${
    item?.file_directory || ""
  }`.toLowerCase();
  return blob.includes("thermal");
}

const FeeReceiptSettings = () => {
  const [settings, setSettings] = useState([]);
  const [thermalSamples, setThermalSamples] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getFeeReceiptSettingsData();
    getThermalSamples();
  }, []);

  //Created By Cursor
  const getFeeReceiptSettingsData = async () => {
    try {
      setLoading(true);
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getFeeReceiptSettingsData.php`;
      const { data: response } = await axios.get(URL, {
        params: { school_code: SCHOOL_CODE },
      });
      if (response.api_status === "OK") {
        setSettings(Array.isArray(response.data) ? response.data : []);
      } else {
        myToast.notify_warning(response.api_remark);
      }
    } catch (error) {
      console.error("Error fetching fee receipt settings:", error);
    } finally {
      setLoading(false);
    }
  };

  //Created By Cursor
  const getThermalSamples = async () => {
    try {
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getPrintableSample.php`;
      const { data: response } = await axios.get(URL, {
        params: { school_code: SCHOOL_CODE, document_type: "fee_receipt" },
      });
      if (response.api_status === "OK") {
        const all = Array.isArray(response.data) ? response.data : [];
        setThermalSamples(all.filter(isThermalSample));
      }
    } catch (error) {
      console.error("Error fetching thermal samples:", error);
    }
  };

  //Created By Cursor
  const saveSettingValue = async (setting_id, setting_value) => {
    setLoading(true);
    try {
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}updateFeeReceiptSetting.php`;
      const { data: response } = await axios.get(URL, {
        params: {
          school_code: SCHOOL_CODE,
          setting_id,
          status: setting_value,
        },
      });
      if (response.api_status === "OK") {
        setSettings((prev) =>
          prev.map((s) =>
            s.setting_id === setting_id
              ? { ...s, setting_value: String(setting_value) }
              : s
          )
        );
        myToast.notify_success("Fee receipt setting updated");
      } else {
        myToast.notify_warning(response.api_remark || "Update failed");
      }
    } catch (error) {
      console.error("Error updating fee receipt setting:", error);
      myToast.notify_error("Could not update setting");
    } finally {
      setLoading(false);
    }
  };

  //Created By Cursor
  const toggleBooleanSetting = (setting_id, setting_value) => {
    const next = String(setting_value) === "1" ? "0" : "1";
    saveSettingValue(setting_id, next);
  };

  const booleanSettings = useMemo(
    () =>
      (settings || []).filter(
        (s) => String(s.setting_type || "").toLowerCase() === "boolean"
      ),
    [settings]
  );

  const textSettings = useMemo(
    () =>
      (settings || []).filter(
        (s) => String(s.setting_type || "").toLowerCase() === "text"
      ),
    [settings]
  );

  const defaultThermalSampleId =
    textSettings.find((s) => s.setting_id === SETTING_DEFAULT_THERMAL_SAMPLE)
      ?.setting_value || "";

  return (
    <div className="content">
      <div className="content-header">
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i className="fa fa-home" aria-hidden="true" />
              <a href="#">Fee Receipt Settings</a>
            </li>
          </ul>
        </div>
      </div>
      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp"
        style={{ "--mMargin": "30px" }}
      >
        <div className="container FeeRSettings-container">
          <h2 className="FeeRSettings-title">Fee Receipt Settings</h2>
          {loading ? <MyLoader /> : null}

          <div className="row g-3">
            {booleanSettings.map((item) => (
              <div className="col-6" key={item.setting_id}>
                <div className="FeeRSettings-box">
                  <label className="FeeRSettings-label">
                    {item.setting_name}
                  </label>
                  <input
                    type="checkbox"
                    className="FeeRSettings-checkbox"
                    checked={String(item.setting_value) === "1"}
                    onChange={() =>
                      toggleBooleanSetting(item.setting_id, item.setting_value)
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Created By Cursor — Default Thermal Sample ID (STT009) */}
          {textSettings.some(
            (s) => s.setting_id === SETTING_DEFAULT_THERMAL_SAMPLE
          ) ? (
            <div
              className="FeeRSettings-box"
              style={{ marginTop: 18, display: "block", width: "100%" }}
            >
              <label className="FeeRSettings-label" style={{ display: "block" }}>
                Default Thermal Sample
              </label>
              <p style={{ fontSize: 12, color: "#64748b", margin: "4px 0 10px" }}>
                Used as the preferred fee-receipt template for thermal printing /
                auto-select after collection.
              </p>
              <select
                className="form-control"
                style={{ maxWidth: 420 }}
                value={defaultThermalSampleId}
                disabled={loading}
                onChange={(e) =>
                  saveSettingValue(SETTING_DEFAULT_THERMAL_SAMPLE, e.target.value)
                }
              >
                <option value="">— Not set —</option>
                {thermalSamples.map((sample) => (
                  <option key={sample.sample_id} value={sample.sample_id}>
                    {sample.sample_name || sample.sample_id}
                    {sample.sample_id ? ` (${sample.sample_id})` : ""}
                  </option>
                ))}
              </select>
              {thermalSamples.length === 0 ? (
                <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>
                  No thermal printable samples found (name/file containing
                  &quot;Thermal&quot;).
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FeeReceiptSettings;
